/**
 * Per-city copy overrides for the service-city template.
 *
 * Most city page copy comes from ONE shared Contentful `serviceAreaContent`
 * entry rendered across ~365 city pages with {{city}} substitution, so it
 * cannot carry city-specific detail. Anything in here overrides that shared
 * copy for a single city only; cities absent from this file are untouched.
 *
 * `whyBullets`, `serviceDetails` and `trustedChoiceBullets` strings are written
 * "Lead-in — detail". The lead-in is only bolded when the separator is a spaced
 * em dash; a hyphen or a colon there renders the whole line as plain body text.
 *
 * Transcribed from Figma (file E0i14mBBa835mkhvqf3E5M): Wyoming = node 428:38,
 * Jackson = node 414:47. Regenerate with scratchpad/gen_content.py.
 */

const cityPageContent = {
  wyoming: {
    heroTitle: "Local Roofing Contractors in Wyoming, MI",
    heroExcerpt: "Roach Home Improvement's crews bring 20+ years of combined roofing experience to same-day " +
      "emergency roofing throughout Wyoming, MI. From fast roof repair to full residential and " +
      "commercial roofing projects in Wyoming, our local team handles it all with straightforward " +
      "pricing and no surprises.",
    trustStripHeading: "#1 Professional Roofing Contractors in Wyoming, MI",
    trustedChoiceHeading: "Professional Roofing for Wyoming's Homes and Businesses",
    trustedChoiceBody: "Our crews regularly move between residential roofing in Wyoming MI projects, ranch homes near " +
      "Wyoming Park, older bungalows near Godfrey-Lee and commercial roofing work along the retail and " +
      "light-industrial stretches nearby. Few contractors handle both well; we built our local team " +
      "specifically because Wyoming needs both.",
    galleryHeading: "Take a Look at Our Previous Works",
    bodyHeading: "Leading Roofing Contractors in Wyoming, MI",
    bodyIntro: "Backed by 20+ years of combined team experience, we serve homeowners and businesses throughout " +
      "Wyoming and the surrounding West Michigan communities with roofing built to hold up against " +
      "lake-effect snow, ice damming, and everything else this stretch of Kent County sees each year.",
    servicesHeading: "Roof Repair, Replacement, and Installation Services",
    promiseHeading: "The Roach Home Improvement Promise",
    includedHeading: "What is Included in Our Roofing Services in Wyoming, MI",
    emergencyHeading: "Emergency Roof Repair In Wyoming, MI",
    emergencyBody: "Storms off Lake Michigan can hit this part of Kent County hard, and a damaged roof rarely waits " +
      "for a convenient time. When you call for emergency roof repair in Wyoming, our local crew " +
      "responds fast from our Clyde Park Ave office, often within the hour. We focus first on stopping " +
      "the damage, tarping exposed areas and patching active leaks before your insulation, drywall, or " +
      "belongings take on more water. Once the roof is stable, we'll walk you through your options for " +
      "a permanent repair or full replacement.",
    costsClosing: "Homes closer to the 28th Street corridor and older neighborhoods like Godfrey-Lee sometimes " +
      "carry additional cost for deck repair or ventilation upgrades, since older construction in this " +
      "area wasn't always built to current attic-ventilation standards.",
    permitsBody: "Most roof replacements, full tear-offs, re-covers, or any change to decking or ventilation, " +
      "require a permit from the City of Wyoming's building department. Minor repairs, like replacing a " +
      "small section of shingles or a flashing fix, typically fall under Michigan's \"ordinary repairs\" " +
      "exception and don't require one. As a Wyoming-based and GAF-certified roofing contractors, we " +
      "pull the permit under our license for most projects. Michigan law also allows homeowners to pull " +
      "their own permit for an owner-occupied single-family home, if you'd prefer to handle that step " +
      "yourself, we're glad to walk you through what the city will ask for either way. Permit fees are " +
      "scaled to project size and are typically included in your quote upfront, so there's no surprise " +
      "line item later. Skipping the permit step isn't just a compliance issue, it can complicate an " +
      "insurance claim or a home sale down the road if a buyer's inspector asks for permit history you " +
      "don't have.",
    neighborhoodsHeading: "Neighborhoods and Nearby Areas We Serve In Wyoming, MI",
    promiseBody: "We stand behind our work. Our promise to you is a roof that not only looks great but also " +
      // The Figma capture interleaved the "Talk to our experts" sidebar card into
      // this paragraph; Jackson's copy of the same sentence is intact and is what
      // the fragments were removed against.
      "performs flawlessly for years to come. We offer comprehensive warranties and after-service " +
      "support to ensure that you're delighted with your investment.",
    whyBullets: [
      "Expertise and Experience — 20+ years of combined experience with the specific challenges local " +
        "roofs face: heavy lake-effect snow load, ice damming along eaves, and wind off the lake in storm " +
        "season.",
      "Quality Materials — Every shingle, tile, or metal panel comes from manufacturers we trust, no " +
        "cutting corners to hit a lower quote.",
      "Customer-Centric Approach — We listen first, then build a plan around your home, your budget, " +
        "and your timeline, not a one-size-fits-all package.",
    ],
    serviceDetails: [
      "Roof Repair — Storm damage, ice-dam leaks, and wind-lifted shingles are the most common calls we " +
        "get in this part of Kent County. We trace the leak to its source before patching anything, so " +
        "the fix holds through the next lake-effect snow event.",
      "Residential Roofing — Full replacements and repairs across Wyoming's housing stock, from " +
        "mid-century ranches to newer builds near Byron Center.",
      "Commercial Roofing — Flat and low-slope systems for the businesses along 28th street and the " +
        "surrounding commercial pockets, scheduled to minimize disruption during business hours.",
      "Metal Roofing — A durable option increasingly requested by Wyoming homeowners looking to reduce " +
        "long-term maintenance.",
      "New Construction & Additions — Roofing installation for new builds and home additions across the " +
        "service area.",
    ],
    // Services grid cards.
    services: [
      {
        title: "New Roof Installation",
        description: "We deliver expert new roof installation and repairs. Contact us today for a free, no-obligation " +
          "estimate.",
      },
      {
        title: "Roof Repair and Restoration",
        description: "Restore your roof to its original condition with our repair and restoration services. We address " +
          "issues early, offering lasting solutions that extend the life of your roof.",
      },
      {
        title: "Owens Corning Shingle Roofing System Service",
        description: "Premium Owens Corning shingle roofing installations. Featuring durable, long-lasting materials " +
          "backed by industry-leading warranties for maximum protection and peace of mind.",
      },
      {
        title: "Roof Inspection & Maintenance",
        description: "A yearly roof inspection, plus a check after any major storm, helps catch those problems early, " +
          "before they reach your attic or interior walls.",
      },
      {
        title: "Custom Roofing Service",
        description: "Custom roofing solutions for unique architectural designs. Our team brings creative expertise to " +
          "every project, ensuring your vision becomes a reality with precision craftsmanship.",
      },
      {
        title: "Advanced Flat Roofing Systems",
        description: "Specialized flat roofing systems designed for commercial properties. Using advanced materials " +
          "and techniques, we ensure watertight, lasting solutions.",
      },
    ],
    officeCard: [
      { label: "Address:", valueFrom: "address" },
      { label: "Wyoming Office Phone", valueFrom: "phone" },
      { label: "Availability", value: "Same-Day" },
      { label: "Service Area", value: "Wyoming and surrounding areas" },
      { label: "License No:", value: "2101200181" },
      { label: "Emergency Service:", value: "24/7" },
      { label: "Years in business:", value: "20+ years" },
    ],
  },
  jackson: {
    heroTitle: "Your Trusted Roofing Contractor in Jackson, MI",
    heroExcerpt: "Roach Home Improvement has provided same-day emergency roofing throughout Jackson, MI for over " +
      "20 years of combined team experience. From emergency roof repair to full residential and " +
      "commercial roofing projects in Jackson, our local crews handle it all with straightforward " +
      "pricing and no surprises.",
    trustStripHeading: "Top Roofing Company in Jackson, MI",
    trustedChoiceHeading: "Jackson's Roofing Company for Homes and Businesses",
    trustedChoiceBody: "Roofing in Jackson has to hold up to real Michigan weather, heavy snow loads, ice dams in the " +
      "winter, and sudden summer hail and wind. As a local roofing contractor, we've built our " +
      "reputation on honest inspections, upfront pricing, and repairs that actually solve the problem " +
      "instead of covering it up. Whether you need residential roofing in Jackson for a single-family " +
      "home, commercial roofing in Jackson for a storefront or multi-unit property, or a emergency roof " +
      "repair after a storm, our team is equipped to handle the job from inspection to final " +
      "walkthrough.",
    galleryHeading: "Check out Our Previous Works",
    bodyHeading: "Leading Roofing Contractor in Jackson, MI",
    bodyIntro: "At Roach Home Improvement, a solid roof is more than shelter, it's security, comfort, and peace " +
      "of mind. We're proud to serve homeowners and businesses throughout Jackson and Jackson County " +
      "with roofing services built to hold up against Michigan winters and summer storms alike.",
    servicesHeading: "Comprehensive Roofing Services for Jackson Residents",
    promiseHeading: "The Roach Home Improvement Promise",
    includedHeading: "What is Included in Our Roofing Services in Jackson, MI",
    permitsBody: "Most full roof replacements and any structural repair work, replacing decking, rafters, or " +
      "altering the roof structure, require a permit under the City of Jackson's building code. If your " +
      "property is outside city limits, permitting instead falls to your township or Jackson County, " +
      "which follow the Michigan Building Code and generally require the same approvals for structural " +
      "work. As a registered, GAF Certified roofing contractor in Jackson, we pull the permit under our " +
      "license, schedule the required inspections (typically after decking/underlayment and again at " +
      "final completion), and provide you documentation for your records, which your insurance company " +
      "may require if you ever file a storm damage claim.",
    neighborhoodsHeading: "Neighborhoods and Nearby Areas We Serve In Jackson, MI",
    promiseBody: "We stand behind our work. Our promise to you is a roof that not only looks great but also " +
      "performs flawlessly for years to come. We offer comprehensive warranties and after-service " +
      "support to ensure that you're delighted with your investment.",
    whyHeading: "Why Choose Roach Home Improvement for Your Roofing Project in Jackson",
    whyBullets: [
      "Expertise and Experience — 20+ years of hands-on work with the roofing challenges this region " +
        "throws at homes: ice damming, wind-driven rain, heavy snow load.",
      "Quality Materials — Every shingle, tile, or metal panel comes from manufacturers we trust, so " +
        "durability isn't a gamble.",
      "Customer-Centric Approach — We listen first, then build a plan around your budget and your home, " +
        "not a one-size-fits-all package.",
    ],
    // Replaces the shared bullet list in city-trusted-choice.js.
    trustedChoiceBullets: [
      "Straightforward Pricing — No hidden fees, no surprise costs, just clear and honest estimates.",
      "Fully Licensed & Insured — Experienced professionals you can rely on, backed by years of local " +
        "expertise.",
      "Premium Materials — Manufacturer-backed shingles, metal panels, and roofing systems built for " +
        "Michigan winters.",
      "Storm Damage & Insurance Help — We handle inspections, paperwork, and insurance claims so you " +
        "don't have to worry.",
      "Jackson's Trusted Choice — Years of experience with the specific roofing challenges Jackson " +
        "homes face, from ice damming to wind-driven rain.",
    ],
    serviceDetails: [
      "Roof Repair — Storm damage, leaks, and missing shingles don't wait. We diagnose the root cause, " +
        "not just the symptom, so the problem doesn't come back next season.",
      "Residential Roofing — From architectural shingles to cedar shake, we handle full replacements " +
        "and installs for single-family homes across Jackson.",
      "Commercial Roofing — Flat and low-slope systems built for local businesses, with minimal " +
        "disruption to daily operations.",
      "Metal Roofing — Longevity, efficiency, and a modern look, available in a range of styles and " +
        "finishes.",
      "Roofing Installation — New builds and additions get the same attention to detail as a full " +
        "replacement, done right the first time.",
    ],
    // Services grid cards.
    services: [
      {
        title: "Tile Roofing Installation",
        description: "We deliver expert tile roof installation and repairs. Contact us today for a free, no-obligation " +
          "estimate.",
      },
      {
        title: "Roof Repair and Restoration",
        description: "Restore your roof to its original condition with our repair and restoration services. We address " +
          "issues early, offering lasting solutions that extend the life of your roof.",
      },
      {
        title: "Owens Corning Shingle Roofing System Installation",
        description: "Premium Owens Corning shingle roofing installations. Featuring durable, long-lasting materials " +
          "backed by industry-leading warranties for maximum protection and peace of mind.",
      },
      {
        title: "Roof Inspections",
        description: "Regular roof inspections catch small issues before they turn into costly repairs or complicate " +
          "an insurance claim.",
      },
      {
        title: "Custom Roofing",
        description: "Custom roofing solutions for unique architectural designs. Our team brings creative expertise to " +
          "every project, ensuring your vision becomes a reality with precision craftsmanship.",
      },
      {
        title: "Advanced Flat Roofing System Services",
        description: "Specialized flat roofing systems designed for commercial properties. Using advanced materials " +
          "and techniques, we ensure watertight, lasting solutions.",
      },
    ],
    officeCard: [
      { label: "Address:", valueFrom: "address" },
      { label: "Jackson Office Phone", valueFrom: "phone" },
      { label: "Availability", value: "Same-Day" },
      { label: "Service Area", value: "Jackson and surrounding areas" },
      { label: "License No:", value: "2101200181" },
      { label: "Emergency Service:", value: "24/7" },
      { label: "Years in business:", value: "20+ years" },
    ],
  },
};

/** Copy overrides for a city, or an empty object when the city has none. */
cityPageContent.forCity = (city) =>
  cityPageContent[String(city || "").trim().toLowerCase()] || {};

module.exports = cityPageContent;
